'use client'

import { useState, useEffect } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { dayHasHours, type OperatingHours } from '@/lib/hours/openStatus'
import { formatTime12h } from './OpenStatusBadge'
import { SectionHeader } from './SectionHeader'

type Props = {
  location: {
    name: string
    timezone: string | null
    address?: string | null
    city?: string | null
    state?: string | null
    phone?: string | null
    /** Horario canónico (HoursEditor). Fuente única — ya no se usa microsite.hours. */
    operating_hours?: OperatingHours | null
  }
}

// Orden de despliegue + clave canónica (coincide con HoursEditor / operating_hours).
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const TODAY_INDEX_TO_KEY = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

function formatFullAddress(address?: string | null, city?: string | null, state?: string | null): string | null {
  const parts = [address, city, state].map((p) => (p ?? '').trim()).filter(Boolean)
  if (parts.length === 0) return null
  return parts.join(', ')
}

export function LocationSection({ location }: Props) {
  const t = useTranslations('public.microsite')
  const locale = useLocale()
  const hours = location.operating_hours ?? null
  const fullAddress = formatFullAddress(location.address, location.city, location.state)
  const mapsHref = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : null
  const phone = (location.phone ?? '').trim()
  const phoneDigits = phone.replace(/\D/g, '')
  const phoneHref = phoneDigits
    ? `tel:+${phoneDigits.startsWith('1') ? phoneDigits : `1${phoneDigits}`}`
    : null

  // Computed client-side only to avoid SSR/client hydration mismatch
  const [todayKey, setTodayKey] = useState<string>('')
  useEffect(() => {
    setTodayKey(TODAY_INDEX_TO_KEY[new Date().getDay()])
  }, [])

  return (
    <section className="py-16 sm:py-24 px-5 sm:px-8 max-w-6xl mx-auto">
      <SectionHeader number="03" kicker={t('locationKicker')} title={t('locationTitle')} />

      {/* Pickup location info — important for online orders / underwriting */}
      {(fullAddress || phone) && (
        <div
          className="mt-8 max-w-md mx-auto border overflow-hidden shadow-sm"
          style={{
            background: 'var(--ms-surface)',
            borderColor: 'var(--ms-border)',
            borderRadius: 'var(--ms-radius)',
          }}
        >
          <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--ms-border)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--ms-text-soft)' }}>Pickup</p>
          </div>
          <div className="px-5 py-4 space-y-2">
            {fullAddress && (
              <p className="text-sm flex items-start gap-2" style={{ color: 'var(--ms-text-soft)' }}>
                <span aria-hidden className="mt-0.5">📍</span>
                {mapsHref ? (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline"
                    style={{ color: 'var(--ms-text)' }}
                  >
                    {fullAddress}
                  </a>
                ) : (
                  <span>{fullAddress}</span>
                )}
              </p>
            )}
            {phone && (
              <p className="text-sm flex items-center gap-2" style={{ color: 'var(--ms-text-soft)' }}>
                <span aria-hidden>📞</span>
                {phoneHref ? (
                  <a href={phoneHref} className="underline-offset-2 hover:underline" style={{ color: 'var(--ms-text)' }}>
                    {phone}
                  </a>
                ) : (
                  <span>{phone}</span>
                )}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Mapa del local — embed sin API key. La ruta /order no aplica CSP estricta
          (ver next.config.ts), así que el iframe de Google Maps carga sin cambios. */}
      {fullAddress && (
        <div
          className="mt-6 max-w-md mx-auto border overflow-hidden shadow-sm"
          style={{
            background: 'var(--ms-surface)',
            borderColor: 'var(--ms-border)',
            borderRadius: 'var(--ms-radius)',
          }}
        >
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--ms-border)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--ms-text-soft)' }}>{t('directions')}</p>
            {mapsHref && (
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold hover:underline"
                style={{ color: 'var(--ms-text-muted)' }}
              >
                {t('openInMaps')} →
              </a>
            )}
          </div>
          <iframe
            title={`${t('directions')} — ${location.name}`}
            src={`https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed&hl=${locale}`}
            width="100%"
            height="220"
            style={{ border: 0, display: 'block' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}

      {hours && Object.keys(hours).length > 0 ? (
        <div
          className="mt-6 max-w-md mx-auto border overflow-hidden shadow-sm"
          style={{
            background: 'var(--ms-surface)',
            borderColor: 'var(--ms-border)',
            borderRadius: 'var(--ms-radius)',
          }}
        >
          <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--ms-border)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--ms-text-soft)' }}>{t('hours')}</p>
          </div>
          <ul>
            {DAY_KEYS.map((key) => {
              const isToday = key === todayKey
              const day = hours[key]
              // Un día sin horas válidas (cerrado, o `{closed:false}` a medias)
              // se muestra como "Cerrado" en vez de reventar con formatTime12h.
              const label = !dayHasHours(day)
                ? t('openStatus.closed')
                : `${formatTime12h(day.open)} – ${formatTime12h(day.close)}`
              return (
                <li
                  key={key}
                  className="flex items-center justify-between px-5 py-3 text-sm border-t first:border-t-0"
                  style={
                    isToday
                      ? { borderColor: 'var(--ms-border)', background: 'var(--ms-surface-2)', fontWeight: 600, color: 'var(--ms-text)' }
                      : { borderColor: 'var(--ms-border)', color: 'var(--ms-text-soft)' }
                  }
                >
                  <span className="capitalize">
                    {t(`days.${key}`)}
                    {isToday && (
                      <span className="ml-2 text-xs uppercase" style={{ color: 'var(--ms-text-muted)' }}>{t('today')}</span>
                    )}
                  </span>
                  <span>{label}</span>
                </li>
              )
            })}
          </ul>
        </div>
      ) : (
        <p className="mt-4 text-center" style={{ color: 'var(--ms-text-muted)' }}>{t('checkHours')}</p>
      )}
    </section>
  )
}
